import 'twin.macro';
import React, { useState } from 'react';
import { useQuery } from 'hooks/sympl-query';
import useNavigationContext from 'hooks/context/nav-context';
import { GET_AD_VERSIONS } from 'graphql/campaign-values/queries';
import { formatPlaces } from 'utils/campaignValues';
import { versionDates, VersionRow } from 'utils/versionDates';

type LocationSpec = { mode?: string; text?: string; names?: string[] } | null;

interface HistoryVersion extends VersionRow {
  version_number: number;
  reason: string | null;
  title: string | null;
  location: LocationSpec;
  template: string | null;
  texts: Record<string, unknown> | null;
  picture_url: string | null;
}

interface AdVersionsResponse {
  adVersions: {
    timezone: string;
    timezone_known: boolean;
    ads: { ad_variant_id: number; versions: HistoryVersion[] }[];
  };
}

const locationLabel = (spec: LocationSpec): string => {
  if (!spec || spec.mode === 'hidden') return 'hidden';
  if (spec.mode === 'custom') return spec.text ?? '';
  return formatPlaces(spec.names ?? []);
};

/**
 * SYM-6351, scene 14 (D35, X-4, P-4). Every version of a corrected ad, for sympl staff only in
 * piece 1: picture, title, texts, location and the days each version counts for. Results per
 * version come with piece 2 (SYM-6352).
 *
 * Hidden for customers here, and refused to them by the server: the panel is a convenience, the
 * server is the protection (sympl-app-context/rules/permissions.md).
 */
const AdHistoryPanel: React.FC = () => {
  const { activeVacancy, isAdmin } = useNavigationContext();
  const [open, setOpen] = useState(false);

  const { data, loading } = useQuery<AdVersionsResponse, { vacancyId: number }>(
    GET_AD_VERSIONS,
    {
      skip: !isAdmin || !open || !activeVacancy,
      variables: { vacancyId: activeVacancy as number },
      fetchPolicy: 'network-only',
    }
  );

  if (!isAdmin) return null;

  const history = data?.adVersions;

  return (
    <div tw="mt-6" data-testid="ad-history">
      <button
        type="button"
        tw="text-sm font-semibold text-gray-700 underline"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Hide ad history' : 'Ad history'}
      </button>

      {open && (
        <div tw="mt-3 flex flex-col gap-4">
          {loading && <p tw="text-sm text-gray-500">Loading…</p>}
          {history && !history.timezone_known && (
            <p tw="text-xs text-gray-500">
              Dates use Brussels time: the ad account&apos;s time zone is not
              known yet.
            </p>
          )}
          {history && !history.ads.length && (
            <p tw="text-sm text-gray-500">
              No ad of this campaign has been updated yet.
            </p>
          )}
          {history?.ads.map((ad) => (
            <div key={ad.ad_variant_id} tw="border rounded-md p-3">
              <p tw="text-sm font-semibold mb-2">Ad {ad.ad_variant_id}</p>
              <div tw="flex flex-col gap-3">
                {ad.versions.map((version) => (
                  <div key={version.version_number} tw="flex gap-3 items-start">
                    {version.picture_url ? (
                      <img
                        src={version.picture_url}
                        alt=""
                        tw="h-24 w-24 object-contain rounded border bg-gray-50"
                      />
                    ) : (
                      <div tw="h-24 w-24 rounded border bg-gray-50" />
                    )}
                    <div tw="text-sm flex flex-col gap-0.5">
                      <span tw="font-semibold">
                        Version {version.version_number}
                        {version.current ? ' (current)' : ''} ·{' '}
                        {versionDates(version)}
                      </span>
                      <span>Title: {version.title || 'hidden'}</span>
                      <span>Location: {locationLabel(version.location)}</span>
                      {typeof version.texts?.text === 'string' && (
                        <span tw="text-gray-600">
                          Text: {version.texts.text as string}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdHistoryPanel;
